import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { Subject } from 'rxjs/internal/Subject';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { sortBy as _sortBy } from 'lodash';

import { Currency, Lesson, Pupil, StudentClass, Box } from '@df/interfaces';
import {
    DEFAULT_TABLE_ROWS,
    TABLE_ROWS_KEY,
    TABLE_ROWS_KEYS,
    TABLE_ROWS_NAMES,
    TaskTypesArray
} from '@app/teacher/components/analysis/analysis-list-subject-home/individual-data/individual.constants';
import { TeacherService } from '@app/teacher/services/teacher.service';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { BsModalService } from 'ngx-bootstrap/modal';
import { BsModalRef } from 'ngx-bootstrap/modal/bs-modal-ref.service';

@Component({
    selector: 'app-individual-data',
    templateUrl: './individual-data.component.html',
    styleUrls: ['./individual-data.component.scss']
})
export class IndividualDataComponent implements OnInit, OnDestroy {
    @ViewChild('commonModal', { static: true }) commonModal: ModalDirective;

    public modalRef: BsModalRef;
    studentClass: StudentClass;
    currentPupil: Pupil;
    selectedTableRows: string[];
    isLoading = true;
    lessonId: string;
    boxes: Array<any>;

    lessonDuration: number;
    studentStat: any;
    studentAmountStat: any;
    studentIncorrectExercises: any[];
    studentIncorrectExercisesGroup: any[];
    individualData: any = { boxes: [] };
    lesson: Lesson;
    currency: Currency;

    plotLessonsStats = [];
    plotBoxesStats = [];

    boxId = null;
    studentId = null;

    private destroy$: Subject<boolean> = new Subject<boolean>();
    public studentsWithResults: Number[] = [];

    constructor(
        private route: ActivatedRoute,
        private teacherService: TeacherService,
        private modalService: BsModalService
    ) {}

    public ngOnInit(): void {
        this.route.parent.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
            const lessonId = params['lesson_id'];
            if (!!lessonId) {
                this.getStudentsWithResults(lessonId);
                this.teacherService
                    .getInitialAnalytics(lessonId)
                    .pipe(takeUntil(this.destroy$))
                    .subscribe(response => {
                        if (response.data && response.data.lesson) {
                            this.lesson = response.data.lesson;
                            this.lessonId = lessonId;
                            this.studentClass = response.data.student_class;
                            this.studentClass.students.sort((student1, student2): number => {
                                return student1.surname > student2.surname ? 1 : -1;
                            });

                            const selectedStudent = JSON.parse(localStorage.getItem('individualDataSelectedStudent'));
                            if (selectedStudent) {
                                this.teacherService.choosePupil(selectedStudent);
                                this.selectPupil(selectedStudent);
                            } else if (this.studentClass && this.studentClass.students.length > 0) {
                                this.getNotAbsentPupil();
                            }
                        }
                    });
            }
        });
        this.teacherService.onSelect.pipe(takeUntil(this.destroy$)).subscribe(data => {
            if ((data === undefined || data === null) && this.studentClass && this.studentClass.students) {
                this.getNotAbsentPupil();
            } else {
                this.selectPupil(data);
            }
        });

        this.loadTableRows();
    }

    public ngOnDestroy(): void {
        this.destroy$.next(true);
    }

    getNotAbsentPupil() {
        let pupil: Pupil;
        for (const student of this.studentClass.students) {
            if (this.studentsWithResults.indexOf(student.id) !== -1) {
                pupil = student;
                break;
            }
        }
        if (pupil !== null) {
            this.selectPupil(pupil);
        }
    }

    selectPupil(pupil: Pupil) {
        this.currentPupil = pupil;
        this.loadPupilData();
        this.loadBoxPlotData();
    }

    selectPupilClick(pupil) {
        if (this.studentsWithResults.indexOf(pupil.id) !== -1) {
            this.teacherService.choosePupil(pupil);
        }
    }

    isPupilAbsent(pupil: Pupil) {
        if (!this.lesson || pupil === undefined) {
            return true;
        }

        return this.studentsWithResults.indexOf(pupil.id) === -1;
    }

    loadPupilData() {
        this.isLoading = true;
        if (!this.lessonId) {
            return;
        }
        this.individualData = { boxes: [] };
        if (this.currentPupil !== undefined) {
            this.teacherService
                .getIndividualPupilAnalytics(String(this.currentPupil.id), this.lessonId)
                .pipe(takeUntil(this.destroy$))
                .subscribe(response => {
                    this.individualData = response;
                    this.boxes = _sortBy(response.boxes, ['level', 'title']);
                    this.currency = response.currency;

                    // Sorting box_data, so they have the same order as boxes
                    if (response.box_data.length) {
                        this.individualData.box_data = response.boxes.map(b => {
                            return response.box_data.find(d => b.id === d.box.id);
                        });
                    }

                    // shortcuts
                    this.lessonDuration = Math.round(response.lesson_duration);
                    this.studentStat = response.student_stat_for_lesson;
                    this.studentAmountStat = response.student_amount_stat;
                    this.studentIncorrectExercises = response.student_incorrect_exercises;
                    this.studentIncorrectExercisesGroup = this.getIncorrectExercisesGroups();
                    this.isLoading = false;
                });

            this.loadPupilPlotData();
        }
        this.isLoading = false;
    }

    loadTableRows() {
        let tableRows: any = window.localStorage.getItem(TABLE_ROWS_KEY);
        if (tableRows) {
            try {
                tableRows = JSON.parse(tableRows);
            } catch (e) {
                tableRows = DEFAULT_TABLE_ROWS;
            }
            this.setTableRows(tableRows);
            return;
        }
        this.setTableRows(DEFAULT_TABLE_ROWS);
    }

    setTableRows(rows: string[] = DEFAULT_TABLE_ROWS) {
        if (!rows) {
            return;
        }
        this.selectedTableRows = rows.filter(r => {
            return TABLE_ROWS_KEYS.indexOf(r) > -1;
        });
        if (!this.selectedTableRows || !Array.isArray(this.selectedTableRows)) {
            this.selectedTableRows = DEFAULT_TABLE_ROWS;
        }
        window.localStorage.setItem(TABLE_ROWS_KEY, JSON.stringify(this.selectedTableRows));
    }

    getRowName(row: string) {
        return TABLE_ROWS_NAMES[row] || '';
    }

    getIncorrectExercisesGroups(): any[] {
        const groups = this.studentIncorrectExercises.reduce((memo, v) => {
            (memo[v.exercise_type] = memo[v.exercise_type] || []).push(v);
            return memo;
        }, {});

        const result = [];

        TaskTypesArray.forEach(taskType => {
            if (groups[taskType]) {
                result.push(groups[taskType]);
            }
        });

        return result;
    }

    openTableSelectRowsDialog(template: TemplateRef<any>) {
        this.modalRef = this.modalService.show(template, Object.assign({}, {}));
    }

    public closeIndividualDataModal(): void {
        this.modalRef.hide();
    }

    public choseIndividualDataTableRows(tableRows) {
        this.setTableRows(tableRows);
    }

    isBoxEmpty(row) {
        switch (row) {
            case 'variantsSolved':
            case 'tasksSolved':
                return false;
            default:
                return true;
        }
    }

    loadPupilPlotData() {
        this.plotLessonsStats = [];
        this.teacherService
            .getIndividualLessonGraph(this.lessonId, String(this.currentPupil.id))
            .pipe(takeUntil(this.destroy$))
            .subscribe(linePlotData => {
                this.plotLessonsStats = [
                    linePlotData.map(data => {
                        if (data.student_data) {
                            return [
                                data.full_lesson_number,
                                data.student_data.total_correct_cards_per_lesson,
                                data.student_data.error_percent_per_lesson,
                                data.student_data.total_amount
                            ];
                        } else {
                            return [data.full_lesson_number, null, null, null];
                        }
                    })
                ];
            });
    }

    loadBoxPlotData() {
        this.plotBoxesStats = null;
        this.teacherService
            .getIndividualGraphPlot(this.lessonId)
            .pipe(takeUntil(this.destroy$))
            .subscribe(boxPlotData => {
                this.plotBoxesStats = boxPlotData.map(boxData => {
                    const studentsData = this.studentClass.students.map(student => {
                        const boxStudentData = boxData.student_data.find(bd => bd.student_id === student.id);
                        return boxStudentData.solved_variants;
                    });

                    return [boxData.box_title, ...studentsData];
                });
            });
    }

    showModal(template: TemplateRef<any>, box, student: Pupil) {
        this.boxId = box.id;
        this.studentId = student.id;
        this.modalRef = this.modalService.show(template, Object.assign({}, {}));
    }

    hideModal($event) {
        if ($event) {
            this.boxId = null;
            this.studentId = null;
        }
        this.modalRef.hide();
    }

    getData(formatingData: any): string {
        try {
            if (!formatingData) {
                return '-';
            }
            return formatingData;
        } catch (e) {
            if (e instanceof TypeError) {
                return '-';
            }
        }
    }

    getBoxAmountData(box_amount_stat) {
        if (box_amount_stat) {
            const amount = box_amount_stat.currencies_amount;
            return amount;
        }
        return '';
    }

    getLessonAmountData(lesson_stat) {
        if (lesson_stat) {
            const amount = lesson_stat.total_currencies_amount;
            return amount;
        }
        return '';
    }

    private getStudentsWithResults(lessonId): void {
        this.teacherService
            .getStudentsWithResults(lessonId)
            .pipe(takeUntil(this.destroy$))
            .subscribe(response => {
                this.studentsWithResults = response.response;
            });
    }

    checkBoxForExtraExercise(box: Box) {
        if (this.individualData.box_data) {
            return this.individualData.box_data.some(item => box.id === item.id && item.extra_exercise);
        }
        return false;
    }
}
